import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Autocomplete,
    Typography,
    Box
} from '@mui/material';
import { useDistributorsContext } from '../../../Contexts/distributorsContext';
import { PutProductDistributorFulfillmentType } from '../hooks/useFetchProductSettingsDistributorOverrides';

const DirectDistributorDialog = ({ open, onClose, onSave, currentDistributor, sku, isChanging = false }) => {
    const { distributors, isReady: areDistributorsReady } = useDistributorsContext();
    const [selectedDirectDistributor, setSelectedDirectDistributor] = useState(null);
    const hasInitialized = useRef(false);

    // Filter to only show direct distributors (active distributors with canOrderBeer = true)
    // Also exclude the current distributor to prevent selecting the same distributor for both direct and indirect
    const directDistributors = distributors?.filter(distributor =>
        distributor.canOrderBeer === true && distributor.distributorId !== currentDistributor.distributorId
    ) || [];

    useEffect(() => {
        if (open && isChanging && currentDistributor.iIndirectDistributorId && !hasInitialized.current) {
            const currentDirectDistributor = directDistributors.find(
                d => d.distributorId === currentDistributor.iIndirectDistributorId
            );
            setSelectedDirectDistributor(currentDirectDistributor || null);
            hasInitialized.current = true;
        } else if (!open) {
            hasInitialized.current = false;
        }
    }, [open, isChanging, currentDistributor.iIndirectDistributorId, directDistributors]);

    const handleSave = async () => {
        if (!selectedDirectDistributor) {
            alert('Please select a direct distributor');
            return;
        }

        try {
            const distributorRow = {
                skuFulfillmentType: { id: 3 } // Indirect fulfillment type
            };

            await PutProductDistributorFulfillmentType(
                { sku: sku },
                { id: currentDistributor.distributorId, ...distributorRow },
                selectedDirectDistributor.distributorId
            );

            onSave(selectedDirectDistributor);
            onClose();
        } catch (error) {
            console.error('Error saving direct distributor:', error);
            alert('Error saving direct distributor');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isChanging ? 'Change Direct Distributor' : 'Select Direct Distributor'}</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        {isChanging
                            ? 'Change the direct distributor that supplies this product for indirect fulfillment.'
                            : 'For indirect fulfillment type, you must select a direct distributor that will supply this product.'
                        }
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                        Note: The direct distributor must be different from the current indirect distributor.
                    </Typography>
                </Box>
                <Autocomplete
                    options={directDistributors}
                    getOptionLabel={(option) => `${option.sapId} - ${option.name}`}
                    value={selectedDirectDistributor}
                    onChange={(_event, newValue) => setSelectedDirectDistributor(newValue)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Direct Distributor"
                            placeholder="Search for a direct distributor..."
                            disabled={!areDistributorsReady}
                        />
                    )}
                    loading={!areDistributorsReady}
                    noOptionsText="No direct distributors found"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={!selectedDirectDistributor}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

DirectDistributorDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    currentDistributor: PropTypes.object.isRequired,
    sku: PropTypes.string.isRequired,
    isChanging: PropTypes.bool
};

export default DirectDistributorDialog; 